/* eslint-disable no-param-reassign */
import _, { isBoolean, toInteger } from 'lodash';
import {
    CommonAdobeAnalyticsUtil, SpmAdobeAnalyticsUtil
} from 'saga-modules';
import SpmConstants from 'sg-spm-constants';

const SPM_TAGGING_PROPERTIES = {
    // vehicle page tagging
    doYouKnowVehicleRegistration: {
        id: 'doYouKnowVehicleRegistration',
        getValue: (value) => value !== false
    },
    Registration: {
        id: 'vehicleRegistration',
        dependent: ['doYouKnowVehicleRegistration']
    },
    make: { id: 'vehicleMake' },
    model: { id: 'vehicleModel' },
    yearOfRegistration: {
        id: 'vehicleRegistrationYear',
        transform: (value) => toInteger(value)
    },
    engineCapacity: {
        id: 'vehicleEngineCapacity',
        transform: (value) => toInteger(value)
    },
    yearPurchased: {
        id: 'vehiclePurchaseYear',
        transform: (value) => toInteger(value)
    },
    vehicleUse: {
        id: 'vehicleUse',
        transform: (value) => {
            value = CommonAdobeAnalyticsUtil.getTypelistValue('VehicleUse_Ext', value);
            if (value) {
                return value === 'Commuting -  including Social, Domestic & Pleasure' ? 'SDP + Commuting' : value.replace(' excluding commuting', '');
            }
            return '';
        }
    },
    mileage: {
        id: 'vehicleAnnualMileage',
        transform: (value) => {
            value = CommonAdobeAnalyticsUtil.getTypelistValue('Mileage_Ext', value);
            if (value) {
                value = value === 'Over 50000' ? '50001' : value.split('-')[1].replace(/\s/g, '').slice(0, -5).replace(/\D/g, '');
            }
            return value;
        }
    },
    vehicleValue: {
        id: 'vehicleValue',
        transform: (value) => (_.isString(value.amount) ? toInteger(value.amount.replace(',', '')) : value.amount)
    },
    isTrackerInstalled: { id: 'trackerInstalled' },
    hasModifications: { id: 'vehicleMods' },
    vehicleModifications: { id: 'vehicleModifications' },
    ncdYears: {
        id: 'noClaimsDiscountInYears',
        transform: (value) => {
            value = value.code || value;
            return toInteger(value.replace('_plus', ''));
        }
    },
    hasLegalCover: { id: 'legalCoverIncluded' },
    hasKeyProtection: { id: 'keyProtectionIncluded' },
    hasFixedPrice: { id: 'fixedPriceSelected' },
    vehicleParkingType: {
        id: 'vehicleOvernightParking',
        transform: (value) => {
            value = CommonAdobeAnalyticsUtil.getTypelistValue('VehicleParkingType_Ext', value);
            if (value) {
                return value === 'Drive/carport/unlocked garage'
                    ? 'DriveCarportUnlocked'
                    : value.toLowerCase().split(' ').map((a) => a.charAt(0).toUpperCase() + a.substr(1)).join(' ')
                        .replace(/-|\s/g, '');
            }
            return '';
        }
    },
    ownAnotherCar: { id: 'otherVehicleUseOwnership' },
    requestedVolExcess: {
        id: 'voluntaryExcess',
        transform: (value) => {
            value = value.code ? value.code.toString() : value.toString();
            if (!value.includes('£')) {
                value = value === '050' ? '£50' : `£${value}`;
            }
            return value;
        }
    },
    hasBreakdownCover: { id: 'breakdownCoverIncluded' },
    hasPersonalAccidentCover: { id: 'personalAccidentCoverIncluded' },
    hasRCECover: { id: 'replacementCarCoverIncluded' },
    hasVandalismPromise: {
        id: 'vandalismPromiseIncluded',
        transform: (value) => value === true
    },
    // driver page tagging
    employmentStatusSelectPolicyHolder: {
        id: 'mainDriverEmploymentStatus',
        transform: (value) => {
            return CommonAdobeAnalyticsUtil.getTypelistValue('EmploymentStatus_Ext', value);
        }
    },
    occupationTypeSelectPolicyHolder: {
        id: 'mainDriverOccupation',
        transform: (value) => {
            return value.code ? CommonAdobeAnalyticsUtil.getTypelistValue('OccupationType_Ext', value.code) : undefined;
        }
    },
    hasClaimsPolicyHolder: { id: 'anyClaimsAccidentsIncidents' },
    claims: { id: 'claims' },
    hasConvictionsPolicyHolder: { id: 'anyMotoringConvictionsFixedPenalityEndorsements' },
    previousInsurerSelect: {
        id: 'previousInsurer',
        transform: (value) => {
            return value.code ? CommonAdobeAnalyticsUtil.getTypelistValue('MotorPreviousInsurer_Ext', value.code) : undefined;
        }
    },
    isPHLegalOwner: {
        id: 'mainDriverLegalOwner',
        transform: (value) => {
            return isBoolean(value) ? value : value === SpmConstants.OWNER_TYPE_PH;
        }
    },
    isMainUserOfVehicle: { id: 'mainDriver' },
    hasAdditionalDrivers: {
        id: 'additionalDrivers',
        dependent: ['additionalDriverCount', 'spouseOrPartner']
    },
    additionalDriverCount: {
        id: 'additionalDriverCount',
        getValue: (value, model) => (value === true && model ? model.length - 1 : undefined),
    },
    multiDriverMainDriverAssignment: {
        id: 'mainDriverOfCar',
        transform: (driver) => { return _.isString(driver) ? driver.slice(0, -2) : driver.driverName; }
    },
    multiDriverLegalOwnerAssignment: {
        id: 'mainDriverLegalOwner',
        transform: (value) => {
            return CommonAdobeAnalyticsUtil.getTypelistValue('VehicleOwnerType_Ext', value);
        }
    },
    accVolExcessCovTerm: { id: 'totalAccidentExcess' },
    ukResidentSinceBirthPolicyHolder: { id: 'ukResidentSinceBirth' },
    fullUkLicencePolicyHolder: {
        id: 'mainDriverFullUkLicence',
        transform: (value) => {
            if (_.isObject(value)) {
                value = value.code ? value.code === SpmConstants.LICENCE_SCOPE_FULL_UK : undefined;
            }
            return value;
        },
        dependent: ['drivingLicenceTypeSelectPolicyHolder']
    },
    drivingLicenceTypeSelectPolicyHolder: {
        id: 'mainDriverOtherLicence',
        transform: (value) => {
            value = CommonAdobeAnalyticsUtil.getTypelistValue('DriverLicenseScope_Ext', value);
            return value === SpmConstants.LICENCE_SCOPE_NO_LICENCE
                ? value.toLowerCase().split(' ').map((a) => a.charAt(0).toUpperCase() + a.substr(1)).join(' ')
                : value;
        },
        getValue: (value) => {
            if (value !== true) {
                value = CommonAdobeAnalyticsUtil.getTypelistValue('DriverLicenseScope_Ext', value);
                return value === SpmConstants.LICENCE_SCOPE_NO_LICENCE
                    ? value.toLowerCase().split(' ').map((a) => a.charAt(0).toUpperCase() + a.substr(1)).join(' ')
                    : value;
            }
            return '';
        }
    },
    drivingLicenceYearsSelectPolicyHolder: {
        id: 'mainDriverLengthLicence',
        transform: (value) => {
            return CommonAdobeAnalyticsUtil.getTypelistValue('DriverLicenseYears_Ext', value);
        }
    },
    hasAccessToOtherVehiclesPolicyHolder: { id: 'otherVehicleUseOwnership' },
    insuranceDeclined: { id: 'declinedPolicy' },
    hasNonMotoringConvictionsPolicyHolder: { id: 'anyUnspentNonMotoringConvictions' },
    payByInstalments: {
        id: 'paymentByInstalments',
        transform: (value) => {
            if (value !== undefined) {
                return value === true ? 'Monthly' : 'Annual';
            }
            return '';
        }
    },
    spouseOrPartner: {
        id: 'additionalDriverSpousePartner',
        transform: (value, model) => {
            if (model === undefined) {
                value = value === true ? 'yes' : undefined;
            }
            return value;
        },
        getValue: (value, model) => {
            if (value && model) {
                value = _.some(model, (driver) => {
                    return driver.isPolicyHolder !== true && driver.isSpouseOrPartner === true;
                });
                return value;
            }
            return '';
        }
    },
    coverLevel: {
        id: 'coverLevel',
        transform: (value) => (value ? CommonAdobeAnalyticsUtil.getTypelistValue('SPMBasicCoverOptions_Ext', value) : undefined),
        dependent: ['product']
    },
    product: {
        id: 'product',
        getValue: (value) => {
            return value ? SpmAdobeAnalyticsUtil.buildProductName(CommonAdobeAnalyticsUtil.getTypelistValue('SPMBasicCoverOptions_Ext', value))
                : undefined;
        }
    },
    pncdOnStandardIncluded: { id: 'pncdOnStandardIncluded' },
    monthlyUnavailableMsgClicked: { id: 'monthlyUnavailableMsgClicked' },
    regLookupStatus: { id: 'regLookupStatus' },
    caisResponse: { id: 'caisResponse' },
    caisModalInteraction: { id: 'caisModalInteraction' },
    quoteNumber: { id: 'quoteNumber' },
    hasAbilityToPay: { id: 'hasAbilityToPay' },
    hasDocuSign: { id: 'hasDocuSign' }
};

export default SPM_TAGGING_PROPERTIES;
